import { ToggleFiltersButton } from '@dealstack/frontend/shared/components/Table/MantineTable/ToggleFiltersButton/ToggleFiltersButton';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { RecordAny } from '@dealstack/shared/lib/common/common.ts';
import {
	ActionIcon,
	Flex,
	MantineProvider,
	rem,
	Text,
	TextInput,
	useMantineTheme,
} from '@mantine/core';
import { Table as MRT_TableInstance } from '@tanstack/react-table';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { useClickOutside } from '@mantine/hooks';
import { emotionTransform } from '@mantine/emotion';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';

interface Props<T extends RecordAny> {
	table: MRT_TableInstance<T>;
	customActions?: JSX.Element;
	hideFiltersButton?: boolean;
	hideSearchBar?: boolean;
}

const FilterWrapper = styled.div`
	width: 100%;

	input {
		margin: 0;
		padding: 0;
		background-color: transparent;

		&:focus {
			box-shadow: none !important;
		}
	}
`;

export const BasicMantineTopToolbar = <T extends RecordAny>({
	table,
	customActions,
	hideFiltersButton = false,
	hideSearchBar = false,
}: Props<T>): JSX.Element => {
	const tCommonUi = useTranslationCommonUi();

	const globalTheme = useMantineTheme();

	const globalFilterInputWrapperRef = useRef<HTMLDivElement>(null);
	const [showFilter, setShowFilter] = useState(false);
	const searchContainerRef = useClickOutside(() => setShowFilter(false));

	const globalFilterValue = (table.getState().globalFilter as string) ?? '';

	const toggleShowFilter = () => {
		setShowFilter((prev) => !prev);
		setTimeout(() => {
			if (globalFilterInputWrapperRef.current) {
				const inputElement = globalFilterInputWrapperRef.current.querySelector('input');
				if (inputElement) {
					inputElement.focus();
				}
			}
		}, 100);
	};

	// v7: a nested provider must re-declare stylesTransform or every `sx` below is dropped.
	return (
		<MantineProvider theme={globalTheme} stylesTransform={emotionTransform}>
			<Flex
				sx={(theme) => ({
					borderTopLeftRadius: theme.radius.md,
					borderTopRightRadius: theme.radius.md,
					flexDirection: 'row',
					gap: theme.spacing.sm,
					justifyContent: 'space-between',
					alignItems: 'stretch',
					backgroundColor: theme.colors.gray[0],
					border: `1px solid ${theme.colors.gray[2]}`,
				})}
			>
				<Flex align="stretch" gap="xs" style={{ flex: 1, overflow: 'hidden' }}>
					{!hideSearchBar && (
						<Flex
							align="center"
							style={{
								flex: 1,
								display: 'flex',
								height: '36px',
								padding: globalTheme.spacing.xs,
								margin: 'auto',
								overflow: 'hidden',
								borderRadius: rem('4px'),
								cursor: 'pointer',
								marginLeft: '1rem',
								...(showFilter
									? {
											backgroundColor: defaultTheme.v3.colors.white[0],
											border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
										}
									: {}),
							}}
							onClick={toggleShowFilter}
							ref={searchContainerRef}
						>
							<Icon variant="search-v3" size="xl" />

							{showFilter ? null : (
								<Flex align="center" style={{ overflow: 'hidden' }}>
									<Text
										sx={(theme) => ({
											paddingLeft: theme.spacing.xs,
										})}
										style={{
											whiteSpace: 'nowrap',
										}}
									>
										{globalFilterValue ??
											tCommonUi('mantine_table.search_input_placeholder')}
									</Text>
								</Flex>
							)}
							{showFilter && (
								<FilterWrapper
									ref={globalFilterInputWrapperRef}
									onClick={(ev) => ev.stopPropagation()}
									style={{ flex: 1 }}
								>
									<TextInput
										placeholder={tCommonUi(
											'mantine_table.search_input_placeholder'
										)}
										value={(table.getState().globalFilter as string) ?? ''}
										onChange={(e) =>
											table.setGlobalFilter(e.currentTarget.value ?? '')
										}
										styles={{ input: { border: 'none' } }}
										variant="unstyled"
										w="100%"
										rightSection={
											<ActionIcon
												variant="transparent"
												onClick={() => table.setGlobalFilter('')}
											>
												<Icon variant="close-v3" size="lg" />
											</ActionIcon>
										}
									/>
								</FilterWrapper>
							)}
						</Flex>
					)}
				</Flex>
				<Flex align="center">
					{hideFiltersButton ? null : (
						<ToggleFiltersButton
							c="dark.0"
							sx={(theme) => ({
								'& span': {
									color: `${theme.colors.dark[8]}`,
								},
								'& svg': {
									color: `${theme.colors.dark[0]}`,
								},
							})}
							leftSection={<Icon variant="filter" size="vlg" />}
							table={table}
						/>
					)}
					{customActions}
				</Flex>
			</Flex>
		</MantineProvider>
	);
};
